// Chart state helpers: symbol resolution, the picker, live rows, redraw modes and the status line. Pure.
import type { ChartPayload } from "../api/types";
import { fmtClock, fmtUtc } from "./format";
import type { DataMode, DrawMode, LiveStatus, SeriesRow, SymbolSeries } from "./types";

export const DEFAULT_SYMBOL = "NVDA";
export const BUTTON_SYMBOLS = 7; // NVDA plus the most liquid feeds, then NBIS
export const ALWAYS_BUTTON = ["NBIS"];
export const DRAW_MS = 2000, GROW_MS = 700, SWITCH_MS = 700;

export type LiveRows = Record<string, SeriesRow[]>;

/** True when the payload came from the worker API (chart.js: dataSource === "api"). */
export const isApiMode = (mode: DataMode): boolean => mode === "live" || mode === "stale";

/** The symbol to draw: the wanted one when the payload has it, else the payload's first, else null. */
export function resolveSymbol(payload: ChartPayload | null, wanted: string): string | null {
  if (!payload) return null;
  if (payload.symbols[wanted]) return wanted;
  return payload.top[0] ?? null;
}

export interface PickerButton {
  sym: string;
  title: string;
  on: boolean;
}
export interface Picker {
  buttons: PickerButton[];
  more: string[];
  /** The current symbol when it is in the "more" list, else "". */
  moreValue: string;
}

export function pickerButtons(payload: ChartPayload, current: string): Picker {
  const top = payload.top.filter((s) => s !== DEFAULT_SYMBOL);
  const syms = [DEFAULT_SYMBOL, ...top.slice(0, BUTTON_SYMBOLS - 1)];
  for (const s of ALWAYS_BUTTON) if (payload.symbols[s] && !syms.includes(s)) syms.push(s);
  const buttons = syms.map((sym) => {
    const e = payload.symbols[sym];
    const name = e?.name ?? sym;
    const liq = Math.round(e?.liquidity_usd ?? 0).toLocaleString("en-US");
    return { sym, title: `${name}, pool liquidity about ${liq} USD`, on: sym === current };
  });
  const more = payload.top.filter((s) => !syms.includes(s));
  return { buttons, more, moreValue: more.includes(current) ? current : "" };
}

/** Drop live rows the payload now covers, and symbols the payload no longer has. */
export function pruneLive(live: LiveRows, payload: ChartPayload, lastRecorded: (sym: string) => number): LiveRows {
  const out: LiveRows = {};
  for (const [sym, rows] of Object.entries(live)) {
    if (!payload.symbols[sym]) continue;
    const last = lastRecorded(sym);
    out[sym] = rows.filter((r) => r.t > last);
  }
  return out;
}

export function appendLive(live: LiveRows, sym: string, row: SeriesRow): LiveRows {
  return { ...live, [sym]: [...(live[sym] ?? []), row] };
}

/** What the last draw was made from, to pick the animation of the next one. */
export interface DrawSnapshot {
  payload: ChartPayload | null;
  symbol: string | null;
  mode: DataMode;
  W: number;
  H: number;
  liveCount: number;
  hasScene: boolean;
}

/**
 * The redraw animation, from what changed since the last draw: first payload draws in from the left, a symbol
 * change or an upgrade from recorded to live data switches, a refreshed payload or a live row grows from the last
 * edge, a resize paints at once. Null when nothing that redraws has changed (or there is nothing to draw).
 */
export function drawModeFor(prev: DrawSnapshot | null, next: DrawSnapshot): DrawMode | null {
  if (!next.hasScene) return null;
  if (!prev?.payload) return "first";
  if (prev.symbol !== next.symbol) return "switch";
  if (prev.payload !== next.payload) return !isApiMode(prev.mode) && isApiMode(next.mode) ? "switch" : "grow";
  if (prev.W !== next.W || prev.H !== next.H) return "none";
  if (prev.liveCount !== next.liveCount) return prev.hasScene ? "grow" : "none";
  return null;
}

/** Start and duration of the clip animation: the clip rectangle widens from `from` to `target` over `ms`. */
export function clipAnimation(mode: DrawMode, prevClip: number, target: number): { from: number; ms: number } {
  if (mode === "first") return { from: 0, ms: DRAW_MS };
  if (mode === "switch") return { from: 0, ms: SWITCH_MS };
  if (mode === "grow") return { from: Math.min(prevClip || target, target), ms: GROW_MS };
  return { from: target, ms: 0 };
}

export const clipEase = (u: number): number => (u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2);

/** Text and class of #chart-live. */
export function statusText(series: SymbolSeries, mode: DataMode, status: LiveStatus): { text: string; className: string } {
  const e = series.entry;
  const lastRec = series.rec.at(-1)?.t ?? null;
  const feedId = (e.feed ?? "").slice(0, 10), pairId = (e.pair ?? "no pool").slice(0, 10);
  const upTo = lastRec != null ? fmtUtc(lastRec) : "n/a";
  const source = isApiMode(mode)
    ? `worker API: samples to ${upTo}, refetched every 120 s`
    : `recorded up to ${upTo}${mode === "recorded" ? " (API unreachable)" : ""}`;
  if (status.ok) return { text: `${source}; live: feed ${feedId}… and pool ${pairId}… read at ${fmtClock(status.at)}, next in 60 s`, className: "inst-live on" };
  if (status.ok === false) return { text: `${source}; live reading failed (${status.err}), retrying in 60 s`, className: "inst-live off" };
  return { text: `${source}; live reading pending`, className: "inst-live" };
}
